import { useState, type ReactNode } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useGoogleAdsConnection } from '@/hooks/useGoogleAdsConnection';
import { useSquareConnection } from '@/hooks/useSquareConnection';
import { useXeroConnection } from '@/hooks/useXeroConnection';
import { useEmailSettings } from '@/hooks/useEmailSettings';
import { useTenant } from '@/hooks/useTenant';
import { IntegrationTile, STATUS_TONE_CLASSES, type IntegrationStatus } from '@/components/integrations/IntegrationTile';
import { CtmLogo, EmailLogo, GoogleAdsLogo, SquareLogo, WebFormLogo, WebhookLogo, XeroLogo } from '@/components/integrations/integrationLogos';
import { GoogleAdsIntegrationPanel } from '@/components/integrations/GoogleAdsIntegrationPanel';
import { SquareIntegrationPanel } from '@/components/integrations/SquareIntegrationPanel';
import { XeroIntegrationPanel } from '@/components/integrations/XeroIntegrationPanel';
import { CtmIntegrationPanel } from '@/components/integrations/CtmIntegrationPanel';
import { JobCloseWebhookPanel } from '@/components/integrations/JobCloseWebhookPanel';
import { WebFormIntegrationPanel } from '@/components/integrations/WebFormIntegrationPanel';
import { EmailSendingPanel } from '@/components/integrations/EmailSendingPanel';

type IntegrationId = 'email' | 'google-ads' | 'square' | 'xero' | 'ctm' | 'job-close-webhook' | 'web-form';

interface IntegrationDefinition {
  id: IntegrationId;
  name: string;
  description: string;
  logo: ReactNode;
  status: IntegrationStatus;
  panel: ReactNode;
}

function googleAdsStatus(
  isLoading: boolean,
  status: ReturnType<typeof useGoogleAdsConnection>['integration'],
): IntegrationStatus {
  if (isLoading) return { label: 'Loading…', tone: 'loading' };
  if (!status || status.status === 'revoked') return { label: 'Not connected', tone: 'off' };
  if (status.status === 'connected') return { label: 'Connected', tone: 'connected' };
  if (status.status === 'expired') return { label: 'Token expired', tone: 'error' };
  return { label: 'Error', tone: 'error' };
}

function squareStatus(
  isLoading: boolean,
  isForbidden: boolean,
  status: ReturnType<typeof useSquareConnection>['integration'],
): IntegrationStatus {
  if (isLoading) return { label: 'Loading…', tone: 'loading' };
  if (isForbidden) return { label: 'Admin only', tone: 'off' };
  if (!status || status.status === 'revoked') return { label: 'Not connected', tone: 'off' };
  if (status.status === 'connected') return { label: 'Connected', tone: 'connected' };
  return { label: 'Needs attention', tone: 'error' };
}

function xeroStatus(
  isLoading: boolean,
  isForbidden: boolean,
  status: ReturnType<typeof useXeroConnection>['integration'],
): IntegrationStatus {
  if (isLoading) return { label: 'Loading…', tone: 'loading' };
  if (isForbidden) return { label: 'Admin only', tone: 'off' };
  if (!status || status.status === 'revoked') return { label: 'Not connected', tone: 'off' };
  if (status.status === 'connected') {
    return status.metadata.tenant_id ? { label: 'Connected', tone: 'connected' } : { label: 'Choose organisation', tone: 'ready' };
  }
  return { label: 'Needs attention', tone: 'error' };
}

function emailStatus(
  isLoading: boolean,
  settings: ReturnType<typeof useEmailSettings>['data'],
): IntegrationStatus {
  if (isLoading) return { label: 'Loading…', tone: 'loading' };
  if (!settings) return { label: 'Unavailable', tone: 'off' };
  if (settings.sending_paused) return { label: 'Paused', tone: 'error' };
  if (settings.custom_domain_status === 'verified') return { label: 'Own domain', tone: 'connected' };
  if (settings.custom_domain_status === 'pending') return { label: 'Awaiting DNS', tone: 'ready' };
  return { label: 'Platform address', tone: 'connected' };
}

export function Integrations() {
  const { data: company, isLoading: companyLoading } = useCompanyProfile();
  const { integration: googleAds, isLoading: googleAdsLoading } = useGoogleAdsConnection();
  const { integration: square, isLoading: squareLoading, isForbidden: squareForbidden } = useSquareConnection();
  const { integration: xero, isLoading: xeroLoading, isForbidden: xeroForbidden } = useXeroConnection();
  // Email settings are admin-only (the RPC refuses everyone else), so the
  // tile is only offered to admins.
  const { isAdmin, isSuperAdmin } = useTenant();
  const canManageEmail = isAdmin || isSuperAdmin;
  const { data: emailSettings, isLoading: emailLoading } = useEmailSettings();

  // Returning from an OAuth flow lands here with ?google_ads=..., ?square=...
  // or ?xero=...; open that integration's panel so the user sees the result
  // (the panel itself reads the params, toasts, and strips them from the URL).
  const [openId, setOpenId] = useState<IntegrationId | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('google_ads')) return 'google-ads';
    if (params.has('square')) return 'square';
    if (params.has('xero')) return 'xero';
    return null;
  });

  const hasCtmCredentials = Boolean(company?.ctm_account_id && company?.ctm_credentials_set_at);
  const webhookEnabled = Boolean(company?.outbound_webhook_enabled);

  const integrations: IntegrationDefinition[] = [
    ...(canManageEmail
      ? [
          {
            id: 'email' as const,
            name: 'Email Sending',
            description: 'Your from-address, own domain and reply handling.',
            logo: <EmailLogo />,
            status: emailStatus(emailLoading, emailSettings),
            panel: <EmailSendingPanel />,
          },
        ]
      : []),
    {
      id: 'google-ads',
      name: 'Google Ads',
      description: 'Campaign spend, cost per job and conversions sent back to Google.',
      logo: <GoogleAdsLogo />,
      status: googleAdsStatus(googleAdsLoading, googleAds),
      panel: <GoogleAdsIntegrationPanel />,
    },
    {
      id: 'square',
      name: 'Square',
      description: 'Online invoice payments through your Square account.',
      logo: <SquareLogo />,
      status: squareStatus(squareLoading, squareForbidden, square),
      panel: <SquareIntegrationPanel />,
    },
    {
      id: 'xero',
      name: 'Xero',
      description: 'Invoices, payments and customers kept in step with your books.',
      logo: <XeroLogo />,
      status: xeroStatus(xeroLoading, xeroForbidden, xero),
      panel: <XeroIntegrationPanel />,
    },
    {
      id: 'ctm',
      name: 'CallTrackingMetrics',
      description: 'Call tracking, lead sync and softphone.',
      logo: <CtmLogo />,
      status: companyLoading
        ? { label: 'Loading…', tone: 'loading' }
        : company?.ctm_mode === 'agency'
          ? { label: 'Managed by Steddy', tone: 'connected' }
          : hasCtmCredentials
            ? { label: 'Connected', tone: 'connected' }
            : { label: 'Not connected', tone: 'off' },
      panel: <CtmIntegrationPanel />,
    },
    {
      id: 'job-close-webhook',
      name: 'Job Close Webhook',
      description: 'Push completed jobs to Make, Zapier or any URL.',
      logo: <WebhookLogo />,
      status: companyLoading
        ? { label: 'Loading…', tone: 'loading' }
        : webhookEnabled
          ? { label: 'Enabled', tone: 'connected' }
          : { label: 'Disabled', tone: 'off' },
      panel: <JobCloseWebhookPanel />,
    },
    {
      id: 'web-form',
      name: 'Web Forms',
      description: 'Receive leads from website and landing-page forms.',
      logo: <WebFormLogo />,
      status: { label: 'Ready', tone: 'ready' },
      panel: <WebFormIntegrationPanel />,
    },
  ];

  const active = integrations.find((i) => i.id === openId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-[-0.01em]">Integrations</h2>
        <p className="text-[13px] text-muted-foreground">
          Connect the tools your business runs on. Click an integration to configure it.
        </p>
      </div>

      {companyLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {integrations.map((i) => (
            <Skeleton key={i.id} className="aspect-square w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {integrations.map((i) => (
            <IntegrationTile
              key={i.id}
              name={i.name}
              description={i.description}
              logo={i.logo}
              status={i.status}
              onClick={() => setOpenId(i.id)}
            />
          ))}
        </div>
      )}

      <Sheet open={active !== null} onOpenChange={(open) => !open && setOpenId(null)}>
        <SheetContent className="flex w-full flex-col p-0 sm:max-w-2xl">
          {active && (
            <>
              <SheetHeader className="flex-shrink-0 border-b px-6 py-4">
                <div className="flex items-center gap-3 pr-7">
                  <div className="shrink-0 [&>*]:h-10 [&>*]:w-10">{active.logo}</div>
                  <div className="min-w-0 flex-1 text-left">
                    <SheetTitle className="text-base font-semibold">{active.name}</SheetTitle>
                    <SheetDescription className="truncate text-xs">{active.description}</SheetDescription>
                  </div>
                  <Badge variant="outline" className={cn('shrink-0 text-[11px] font-medium', STATUS_TONE_CLASSES[active.status.tone])}>
                    {active.status.label}
                  </Badge>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-6 py-5">{active.panel}</div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
